import React, { useState, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import * as LucideIcons from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface LeadStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
  display_order: number;
  is_default: boolean;
}

interface PatientLeadFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilters: string[];
  onStatusFiltersChange: (statuses: string[]) => void;
}

export function PatientLeadFilters({ 
  searchTerm, 
  onSearchChange, 
  statusFilters, 
  onStatusFiltersChange 
}: PatientLeadFiltersProps) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    fetchStatuses();
    loadUserPreferences();
  }, []);

  // Save user preferences whenever filters change
  useEffect(() => {
    if (user && statusFilters.length > 0) {
      saveUserPreferences();
    }
  }, [statusFilters, user]);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_lead_statuses');
      
      if (error) throw error;
      
      setStatuses(data || []);
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw error;
      }
      
      if (data?.preferences?.patientLeadFilters) {
        onStatusFiltersChange(data.preferences.patientLeadFilters);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const saveUserPreferences = async () => {
    if (!user) return;
    
    try {
      setSavingPreferences(true);
      
      // First get existing preferences
      const { data: existingData, error: fetchError } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .single();
      
      // Prepare new preferences object
      const newPreferences = {
        ...(existingData?.preferences || {}),
        patientLeadFilters: statusFilters
      };
      
      // Upsert preferences
      const { error: upsertError } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          preferences: newPreferences
        });
      
      if (upsertError) throw upsertError;
    } catch (error) {
      console.error('Error saving user preferences:', error);
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleStatusToggle = (slug: string) => {
    if (statusFilters.includes(slug)) {
      // Remove status if already selected
      onStatusFiltersChange(statusFilters.filter(s => s !== slug));
    } else {
      // Add status if not selected
      onStatusFiltersChange([...statusFilters, slug]);
    }
  };

  const clearFilters = () => {
    onStatusFiltersChange([]);
    
    // Also clear from user preferences
    if (user) {
      saveUserPreferences();
    }
  };

  // Render status filter button with the correct icon and color
  const renderStatusButton = (status: LeadStatus) => {
    const IconComponent = LucideIcons[status.icon as keyof typeof LucideIcons] as React.FC<{ className?: string }> || LucideIcons.Filter;
    const isSelected = statusFilters.includes(status.slug);
    
    return (
      <button
        key={status.id}
        onClick={() => handleStatusToggle(status.slug)}
        className={`px-4 py-2 rounded-lg border-2 transition-colors duration-200 ${
          isSelected
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <IconComponent className="h-4 w-4 inline mr-2" />
        {status.name}
      </button>
    );
  };

  return (
    <div className="bg-white shadow-lg rounded-lg mb-6 p-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {loading ? (
            <div className="flex items-center px-4 py-2 rounded-lg border-2 border-gray-300">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500 mr-2"></div>
              <span className="text-gray-500">Loading...</span>
            </div>
          ) : (
            <>
              {statuses.map(status => renderStatusButton(status))}
              {statusFilters.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <X className="h-4 w-4 inline mr-2" />
                  Clear Filters
                </button>
              )}
            </>
          )}
        </div>
      </div>
      
      {statusFilters.length > 0 && (
        <div className="mt-3 flex items-center">
          <span className="text-sm text-gray-500 mr-2">Active filters:</span>
          <div className="flex flex-wrap gap-1">
            {statusFilters.map(filter => {
              const status = statuses.find(s => s.slug === filter);
              if (!status) return null;
              
              const IconComponent = LucideIcons[status.icon as keyof typeof LucideIcons] as React.FC<{ className?: string }> || LucideIcons.Filter;
              
              return (
                <span 
                  key={filter} 
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  <IconComponent className="h-3 w-3 mr-1" />
                  {status.name}
                  <button 
                    onClick={() => handleStatusToggle(filter)}
                    className="ml-1 text-indigo-600 hover:text-indigo-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
          {savingPreferences && (
            <span className="ml-2 text-xs text-gray-400">Saving preferences...</span>
          )}
        </div>
      )}
    </div>
  );
}